import { useEffect, useRef } from 'react'
import { apiFetch } from '../lib/api.js'
import { useAuth } from './useAuth.js'
import { readGuestProgress, clearGuestProgress } from '../lib/guestProgress.js'
import { trackEvent } from '../lib/tracking.js'

interface ImportResult {
  ok: true
  importedModules: number
  importedSkips: number
  rejected: number
}

/**
 * One-shot, post-login migration of anonymous localStorage progress into the
 * account. The server RE-CHECKS access per item (a forged blob cannot inject
 * `auth`/`paid` completions) and the import is idempotent (keep-best). On
 * success the shadow is cleared and `learn:freemium:progress-migrated` fires.
 * Mount once near the app shell (mirrors `useSeedProfileName`).
 */
export function useGuestProgressMigration(): void {
  const { session } = useAuth()
  const ranRef = useRef(false)

  useEffect(() => {
    if (!session || ranRef.current) return
    const payload = readGuestProgress()
    if (!payload) {
      ranRef.current = true
      return
    }
    ranRef.current = true
    void (async () => {
      try {
        const res = await apiFetch<ImportResult>('/api/public/guest-progress/import', {
          method: 'POST',
          body: payload,
        })
        clearGuestProgress()
        const migrated = (res.importedModules ?? 0) + (res.importedSkips ?? 0)
        if (migrated > 0) {
          trackEvent('learn:freemium:progress-migrated', {
            modules: res.importedModules,
            skips: res.importedSkips,
          })
        }
      } catch {
        // Import failed (e.g. transient network error). Preserve the blob and
        // reset the guard so the migration is re-attempted on the NEXT app load
        // (the effect only re-runs on a session change, so there is no in-session
        // retry — the TTL'd blob simply survives until then).
        ranRef.current = false
      }
    })()
  }, [session])
}
